import {
  assertIsAnEntityDataItem,
  assertHasStringVersionProperty,
  assertIsArrayAndAllObjectsHaveStringVersionProperty,
  assertIsNotUndefined,
  assert,
} from '~/types/models'
import type { THrid, TEntityId, TEntityVersion } from '~/types/models'

/**
 * Writing a save response back onto the objects that were sent.
 *
 * Both functions here were inline blocks in `saveEntityData.ts` — one in
 * `postEntityData`, one in `patchEntityData` — doing the same job for the two
 * verbs. They are lifted out unchanged so that MORE THAN ONE FLOW can reach
 * them: `POST /api/transactions` applies a response made of several
 * typed actions by feeding each one through the same code the single-verb paths
 * use, rather than growing a third copy that drifts from both.
 *
 * ⚠ THEY MUTATE THE CALLER'S OBJECTS IN PLACE, and callers depend on that. The
 * dialog, the card and the propagation cascades all keep references to the rows
 * they submitted and re-render from them; returning new objects instead would
 * leave every one of those holding the pre-save version.
 *
 * ⚠ THE PAIRING IS POSITIONAL. `response[i]` is written onto `sent[i]`. That is
 * a contract the server has to honour, not an implementation detail: a server
 * that reorders rows writes versions onto the wrong entities, and the corruption
 * surfaces later as a 409 on an unrelated save — about as far from its cause as
 * a bug can get. `expectedIds` exists to turn that into an immediate throw; see
 * below for why it is optional.
 */

/**
 * Was every row echoed in the order it was sent?
 *
 * ⚠ OPT-IN, DELIBERATELY. Passing `expectedIds` is what makes the positional
 * contract enforced rather than assumed — but the two single-verb callers do NOT
 * pass it, because they never checked, and turning the check on for them would
 * be a behaviour change smuggled in under a refactor. In particular the create
 * array branch below has never asserted anything about its response and will
 * happily write `undefined` hrids; that stays exactly as it was. New callers
 * pass ids and get the strictness.
 */
function _assertIdsMatch(
  fn: string,
  expectedIds: (TEntityId | undefined)[] | undefined,
  data: any[]
) {
  if (!expectedIds) {
    return
  }
  assert(
    data.length === expectedIds.length,
    `${fn} the response has ${data.length} rows for ${expectedIds.length} sent.`
  )
  expectedIds.forEach((expectedId, index) => {
    assert(
      data[index]?.id === expectedId,
      `${fn} row ${index} came back as '${data[index]?.id}' but` +
        ` '${expectedId}' was sent. The response is not in request order;` +
        ` writing it positionally would put versions on the wrong rows.`
    )
  })
}

/**
 * A CREATE response: hrid and version onto the rows that were sent.
 *
 * Note: Given the live server now returns the entire IEntityDataItem or
 * IEntityDataItem array (not just a THridAndTVersion object), we should
 * really just return that data from this function, rather than manipulating
 * the input newEntityData to include the returned hrid and version. But
 * that's too big a change to do at this moment. Should definitely be done
 * in the future. JR.
 */
export function writeBackHridsAndVersions({
  fn,
  newEntityData,
  data,
  expectedIds,
}: {
  fn: string
  newEntityData: any
  data: any
  expectedIds?: (TEntityId | undefined)[]
}): void {
  if (!Array.isArray(newEntityData)) {
    assertIsAnEntityDataItem(fn, data) // Type guard; should always be
    const { hrid, version } = data
    console.debug(`${fn} Got hrid '${hrid}'. Got version '${version}'`)
    console.debug(`${fn} Adding hrid and version to newEntityData.`)
    newEntityData.hrid = hrid
    newEntityData.version = version
  } else {
    _assertIdsMatch(fn, expectedIds, data)
    const [hridArray, versionArray] = data.reduce(
      (
        [a, b]: [THrid[], TEntityVersion[]],
        { hrid, version }: { hrid: THrid; version: TEntityVersion }
      ) => {
        a.push(hrid)
        b.push(version)
        return [a, b]
      },
      [[], []]
    )
    console.debug(`${fn} Got hrid array '${hridArray}'`)
    console.debug(`${fn} ${fn} Got version array '${versionArray}'`)
    console.debug(`Adding both to newEntityData items.`)
    newEntityData.forEach((ned: any, index: number) => {
      ned.hrid = hridArray[index]
      ned.version = versionArray[index]
    })
  }
}

/**
 * An UPDATE response: the new version onto the rows that were sent.
 *
 * Note: Given the live server now returns the entire updatedEntityData array
 * (not just a versions array), we should really just return that data from
 * this function, rather than manipulating the input newEntityData to include
 * the returned version. But that's too big a change to do at this moment.
 * Should definitely be done in the future. JR.
 */
export function writeBackVersions({
  fn,
  updatedEntityData,
  data,
  expectedIds,
}: {
  fn: string
  updatedEntityData: any
  data: any
  expectedIds?: (TEntityId | undefined)[]
}): void {
  if (!Array.isArray(updatedEntityData)) {
    assertHasStringVersionProperty(fn, data) // always should
    const { version } = data
    console.debug(`${fn} Got version '${version}'`)
    console.debug(
      `${fn} Swapping new version (from request response)` +
        ` for old version in non-array updatedEntityData.`
    )
    updatedEntityData.version = version
  } else {
    assertIsArrayAndAllObjectsHaveStringVersionProperty(fn, data)
    _assertIdsMatch(fn, expectedIds, data)
    const versionArray = data.map(
      (returnedUpdatedEntityData: any) => returnedUpdatedEntityData.version
    )
    console.debug(`${fn} Got version array`, versionArray)
    console.debug(
      `${fn} Swapping new version strings for old in updatedEntityData items array.`
    )
    updatedEntityData.forEach((ued: any, index: number) => {
      assertIsNotUndefined(ued)
      ued.version = versionArray[index]
    })
  }
}
